import { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { ArrowLeft, Fuel, ChevronRight, ChevronDown, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useAuth } from '@/lib/auth';
import { DriverCell, CAN_ASSIGN, type PickableDriver } from '@/components/common/VehicleCell';

/**
 * Dumper fuel: one row per truck, opening to its fills.
 *
 * Shaped like the driver-pay sheet on purpose - a list you scan, and open only
 * the one you are asking about. A flat table of 454 transactions answers "what
 * happened" and never answers "what has this truck cost", which is the question
 * people actually arrive with.
 *
 * The driver on each row can be changed here, through the same component and
 * the same endpoint the Sides screen uses. One truck has one driver, enforced
 * in the database, so doing it from either screen cannot produce two.
 */

interface Txn {
  id: string;
  at: string;
  provider: string;
  liters: number;
  sar: number;
  station: string | null;
  card: string | null;
  driverName: string | null;
  driverFromAssignment: boolean;
}

interface Row {
  vehicleId: string;
  plate: string;
  vehicleCode: string;
  owner: string;
  driverId: string | null;
  driverName: string | null;
  driverCode: string | null;
  fills: number;
  liters: number;
  sar: number;
  lastFill: string | null;
  transactions: Txn[];
}

const nf = (n: number) => Number(n || 0).toLocaleString('en-US');
const day = (s: string | null) =>
  (s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

export function DumperFuelPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  // One open row at a time: two sets of transactions on screen is noise, and
  // the question is always about one truck.
  const [openId, setOpenId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<Record<string, string>>({});

  const canAssign = CAN_ASSIGN.includes(user?.role ?? '');

  const { data, isLoading } = useQuery({
    queryKey: ['dumper-fuel'],
    queryFn: async () => {
      const { data } = await apiClient.get<{
        items: Row[];
        totals: { vehicles: number; withFuel: number; fills: number; liters: number; sar: number };
      }>('/api/v1/dumpers/fuel');
      return data;
    },
    staleTime: 60 * 1000,
  });

  const { data: drivers } = useQuery({
    queryKey: ['pickable-drivers'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: PickableDriver[] }>(
        '/api/v1/driver-vehicle-assignments/pickable-drivers',
      );
      return data.items ?? [];
    },
    enabled: canAssign,
    staleTime: 5 * 60 * 1000,
  });

  /** Assign or clear, then refresh both this list and the picker. */
  const run = async (vehicleId: string, fn: () => Promise<{ data: unknown }>) => {
    setBusy(vehicleId); setError(null);
    setAssignError((p) => ({ ...p, [vehicleId]: '' }));
    try {
      const res = await fn();
      // The server says when it took the driver off another truck. Passing that
      // on matters: a reassignment is never only about the row you clicked.
      const warnings = (res.data as { warnings?: string[] })?.warnings ?? [];
      if (warnings.length) setNote(warnings.join(' '));
      await qc.invalidateQueries({ queryKey: ['dumper-fuel'] });
      await qc.invalidateQueries({ queryKey: ['pickable-drivers'] });
      void qc.invalidateQueries({ queryKey: ['sides-roster'] });
    } catch (e) {
      setAssignError((p) => ({ ...p, [vehicleId]: extractErrorMessage(e) }));
    } finally {
      setBusy(null);
    }
  };

  const items = data?.items ?? [];
  const t = data?.totals;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2"><LanguageSwitcher /><ThemeToggle /></div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
        <div className="flex items-center gap-3">
          <Fuel className="h-7 w-7 text-gold" />
          <h1 className="font-display text-3xl text-foreground">Dumper fuel</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-6">
          Every truck on the Dumpers side. Click a row to see its fills. The driver can be changed here.
        </p>

        {t && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { k: 'Trucks', v: `${t.withFuel} of ${t.vehicles} fuelling` },
              { k: 'Fills', v: nf(t.fills) },
              { k: 'Litres', v: nf(t.liters) },
              { k: 'Total', v: `${nf(t.sar)} SAR` },
            ].map((c) => (
              <div key={c.k} className="rounded-lg border border-border bg-card p-3">
                <div className="text-xs text-muted-foreground">{c.k}</div>
                <div className="text-lg font-medium text-foreground">{c.v}</div>
              </div>
            ))}
          </div>
        )}

        {note && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
            {note}
            <button type="button" className="ms-3 underline text-xs" onClick={() => setNote(null)}>dismiss</button>
          </div>
        )}
        {error && <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm">{error}</div>}

        {isLoading && <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="w-8 p-3"></th>
                <th className="text-start p-3 font-medium">Driver</th>
                <th className="text-start p-3 font-medium">Vehicle</th>
                <th className="text-end p-3 font-medium">Fills</th>
                <th className="text-end p-3 font-medium">Litres</th>
                <th className="text-end p-3 font-medium">SAR</th>
                <th className="text-start p-3 font-medium">Last fill</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No trucks on the Dumpers side.</td></tr>
              )}
              {items.map((r) => {
                const isOpen = openId === r.vehicleId;
                return (
                  <Fragment key={r.vehicleId}>
                    <tr
                      onClick={() => setOpenId(isOpen ? null : r.vehicleId)}
                      className={`border-t border-border cursor-pointer hover:bg-muted/30 ${isOpen ? 'bg-muted/20' : ''}`}
                    >
                      <td className="p-3 text-muted-foreground">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                      <td className="p-3">
                        <DriverCell
                          vehicleId={r.vehicleId}
                          driverName={r.driverName}
                          canAssign={canAssign}
                          drivers={drivers ?? []}
                          onAssign={(vehicleId, driverId) => void run(vehicleId, () =>
                            apiClient.post('/api/v1/driver-vehicle-assignments', { driverId, vehicleId }))}
                          // Unassign takes the DRIVER's id - the existing
                          // endpoint works that way and there is no reason to
                          // add a second one that means the same thing. The row
                          // already knows who is on the truck.
                          onUnassign={() => { if (r.driverId) void run(r.vehicleId, () =>
                            apiClient.post('/api/v1/driver-vehicle-assignments/unassign', { driverId: r.driverId })); }}
                          busy={busy === r.vehicleId}
                          error={assignError[r.vehicleId] || null}
                        />
                        {r.driverCode && (
                          <div className="text-[11px] text-muted-foreground font-mono">{r.driverCode}</div>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="font-mono" dir="ltr">{r.plate}</span>
                        {r.owner && r.owner !== 'jasr' && (
                          <span className="ms-2 text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                            AK
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-end tabular-nums">{r.fills || '—'}</td>
                      <td className="p-3 text-end tabular-nums">{r.fills ? nf(r.liters) : '—'}</td>
                      <td className="p-3 text-end tabular-nums font-medium">{r.fills ? nf(r.sar) : '—'}</td>
                      <td className="p-3 text-muted-foreground">
                        {r.fills ? day(r.lastFill) : <span className="text-xs">no card data</span>}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr className="border-t border-border bg-muted/10">
                        <td colSpan={7} className="p-0">
                          {r.transactions.length === 0 ? (
                            <div className="p-6 text-sm text-muted-foreground">
                              No fuel recorded for {r.plate}. Its plate is not registered with PetroApp
                              or Fleet Plus — nothing has been sent for it.
                            </div>
                          ) : (
                            <table className="w-full text-xs">
                              <thead className="text-muted-foreground">
                                <tr>
                                  <th className="text-start px-4 py-2 font-medium">Date</th>
                                  <th className="text-start px-4 py-2 font-medium">Driver</th>
                                  <th className="text-start px-4 py-2 font-medium">Station</th>
                                  <th className="text-start px-4 py-2 font-medium">Card</th>
                                  <th className="text-end px-4 py-2 font-medium">Litres</th>
                                  <th className="text-end px-4 py-2 font-medium">SAR</th>
                                </tr>
                              </thead>
                              <tbody>
                                {r.transactions.map((x) => (
                                  <tr key={x.id} className="border-t border-border/60">
                                    <td className="px-4 py-2 whitespace-nowrap">{day(x.at)}</td>
                                    <td className="px-4 py-2">
                                      {x.driverName ?? '—'}
                                      {/* A name taken from who held the truck that day is a
                                          different claim from one matched to the fill. */}
                                      {x.driverFromAssignment && (
                                        <span
                                          className="text-muted-foreground"
                                          title="From the vehicle assignment on this date — not matched to the fill itself"
                                        > *</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-muted-foreground">{x.station ?? '—'}</td>
                                    <td className="px-4 py-2 text-muted-foreground font-mono">{x.card ?? '—'}</td>
                                    <td className="px-4 py-2 text-end tabular-nums">{nf(x.liters)}</td>
                                    <td className="px-4 py-2 text-end tabular-nums">{nf(x.sar)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground mt-3">
          A <span className="font-medium">*</span> next to a driver means the name comes from who held
          that truck on the date of the fill, rather than from the fill itself.
        </p>
      </main>
    </div>
  );
}

export default DumperFuelPage;
